import React from 'react';
import { formatDateString } from '../../components/FormattedDateTime/FormattedDateTime';
import { Section, FieldGrid, IdTile, TrailLink } from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';
import { paymentToLabel } from '../../utils/enumLabels';
import { showCurrency } from '../../utils';

const hasValue = (value) => value !== undefined && value !== null && value !== '';

function formatMoney(currency, amount) {
	return `${showCurrency(currency) || ''} ${Number(amount || 0).toLocaleString('es-UY')}`.trim();
}

function AssetSaleInfo({ assetSale }) {
	const { t } = useLabels();
	if (!assetSale) {
		return null;
	}

	const asset = assetSale.asset;

	function verDocumento() {
		if (assetSale.document) {
			window.open(assetSale.document.fileLink, '_blank', 'noopener,noreferrer');
		}
	}

	const left = [
		{
			label: t('field.date'),
			value: formatDateString({ dateValue: assetSale.date || '', dateFormat: 'DD/MM/YYYY' }),
			mono: true,
		},
		{
			label: t('field.amount'),
			value: hasValue(assetSale.amount) ? formatMoney(assetSale.currency, assetSale.amount) : null,
			mono: true,
		},
		{
			label: t('field.totalInDebts'),
			value: hasValue(assetSale.sumDebts) ? formatMoney(assetSale.currency, assetSale.sumDebts) : t('msg.noDebts'),
			mono: true,
		},
		{
			label: t('field.lotUpper'),
			value: assetSale.lot,
			mono: true,
		},
		{
			label: t('form.paymentTo'),
			value: assetSale.paymentTo ? paymentToLabel(t, assetSale.paymentTo) : t('msg.notSpecified'),
		},
		{
			label: t('field.housingUnit'),
			value: assetSale.housingUnit,
		},
	];

	const right = [
		{
			label: t('field.currency'),
			value: assetSale.currency,
			mono: true,
		},
		{
			label: t('field.initialDelivery'),
			value: hasValue(assetSale.initialPayment) ? formatMoney(assetSale.currency, assetSale.initialPayment) : t('msg.notSpecified'),
			mono: true,
		},
		{
			label: t('field.active'),
			value: asset && asset.customId ? (
				<TrailLink to={`/AdminScreen/assets/asset/${asset._id || asset}`}>{asset.customId}</TrailLink>
			) : (
				t('msg.noAssetAssigned')
			),
		},
		{
			label: t('entity.purchaser'),
			value: assetSale.purchaser ? (
				<TrailLink
					to={`/AdminScreen/accounts/account/${assetSale.purchaser._id || assetSale.purchaser}`}
					className='tw:font-medium tw:text-accent'
				>
					{assetSale.purchaser.name}
				</TrailLink>
			) : (
				t('msg.noPurchaserAssigned')
			),
		},
		...(assetSale.bankAccount
			? [{
				label: t('form.paymentAccount'),
				value: `${assetSale.bankAccount.bank} - ${assetSale.bankAccount.accountNumber} (${assetSale.bankAccount.currency})`,
			}]
			: []),
		{
			label: t('field.escritura'),
			value: assetSale.document ? (
				<button
					type='button'
					onClick={verDocumento}
					className='tw:font-medium tw:text-accent tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer'
				>
					{t('action.viewEscritura')}
				</button>
			) : (
				t('msg.noDocument')
			),
		},
		{
			label: t('field.responsible'),
			value: assetSale.responsible ? (
				<span className='tw:inline-flex tw:items-center tw:gap-2'>
					<IdTile name={assetSale.responsible.name} size={22} shape='circle' />
					<span>{assetSale.responsible.name}</span>
				</span>
			) : (
				t('msg.noResponsibleAssigned')
			),
		},
	];

	return (
		<Section title={t('title.details')}>
			<FieldGrid left={left} right={right} />
		</Section>
	);
}

export default AssetSaleInfo;
